import React, { useContext, useEffect, useState } from 'react'
import {
  Button,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import moment from 'moment'
import API from '../../../../services/API'
import BigModal from '../../../../components/Modals/BigModal'
import AppraisalReview from './AppraisalReview'
import { GlobalContext } from '../../../../App.js'

const statusColors = {
  'Awaiting Employee': 'contrast',
  'Awaiting HR Review': 'info',
  Completed: 'success',
  Cancelled: 'default',
}

const NextAppraisalDateCard = ({ employeeID, initialDate }) => {
  const [savedDate, setSavedDate] = useState(
    initialDate ? moment(initialDate).format('YYYY-MM-DD') : '',
  )
  const [date, setDate] = useState(savedDate)
  const [saving, setSaving] = useState(false)

  const { setToast } = useContext(GlobalContext)

  const submitDate = (nextAppraisalDate) => {
    setSaving(true)
    API.put(`/appraisals/employee/${employeeID}/next-date`, {
      nextAppraisalDate: nextAppraisalDate || null,
    })
      .then((res) => {
        if (res.status === 200) {
          const newDate = res.data.nextAppraisalDate
            ? moment(res.data.nextAppraisalDate).format('YYYY-MM-DD')
            : ''
          setSavedDate(newDate)
          setDate(newDate)
          setToast({
            message: newDate ? 'Next Appraisal Date Saved' : 'Date Cleared',
          })
        }
      })
      .finally(() => setSaving(false))
  }

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack gap={1}>
          <Typography variant="subtitle2" fontWeight={600}>
            Next Appraisal Date
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Your organisation sets appraisal dates individually per employee.
            The appraisal will be created and the employee reminded in the
            run-up to this date. When an appraisal completes, the next date is
            set automatically from your appraisal frequency — adjust it here
            whenever a different date suits this employee.
          </Typography>
          <Stack direction="row" gap={1} alignItems="center">
            <TextField
              type="date"
              size="small"
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
            <Button
              variant="contained"
              disabled={saving || !date || date === savedDate}
              onClick={() => submitDate(date)}
            >
              Save
            </Button>
            {savedDate && (
              <Button
                color="error"
                disabled={saving}
                onClick={() => submitDate(null)}
              >
                Clear
              </Button>
            )}
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  )
}

const AppraisalsTab = ({ employee, loadEmployee }) => {
  const [loading, setLoading] = useState(true)
  const [appraisals, setAppraisals] = useState([])
  const [reviewingAppraisalID, setReviewingAppraisalID] = useState(null)
  const [schedulingMode, setSchedulingMode] = useState('automatic')

  const employeeID = employee?.employee?._id

  const loadAppraisals = () => {
    if (!employeeID) return
    API.get(`/appraisals/employee/${employeeID}`)
      .then((res) => {
        if (res.status === 200) setAppraisals(res.data)
      })
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    loadAppraisals()
  }, [employeeID])

  useEffect(() => {
    API.get('/settings?select=staffAppraisalSettings.schedulingMode').then(
      (res) => {
        if (res.status === 200) {
          setSchedulingMode(
            res.data?.staffAppraisalSettings?.schedulingMode ?? 'automatic',
          )
        }
      },
    )
  }, [])

  const handleCompleted = () => {
    loadAppraisals()
    setReviewingAppraisalID(null)
    // In individual scheduling mode, completing a review auto-sets the
    // employee's next appraisal date - refresh so the card shows it
    loadEmployee?.()
  }

  return (
    <Stack gap={2}>
      {schedulingMode === 'individual' && employeeID && (
        <NextAppraisalDateCard
          key={`${employeeID}-${employee?.employee?.nextAppraisalDate ?? ''}`}
          employeeID={employeeID}
          initialDate={employee?.employee?.nextAppraisalDate}
        />
      )}
      {loading ? (
        <LinearProgress />
      ) : (
        <Stack gap={1}>
          {appraisals.length === 0 && (
            <Typography color="text.secondary">
              {schedulingMode === 'individual'
                ? 'No appraisals recorded yet — set a next appraisal date above and one will be created as it approaches.'
                : "No appraisals recorded yet — one will be created automatically when this employee's appraisal is due."}
            </Typography>
          )}
          {appraisals.map((appraisal) => (
            <Card key={appraisal._id} variant="outlined">
              <CardContent>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="body2">
                    Due {moment(appraisal.dueDate).format('Do MMM YYYY')}
                  </Typography>
                  <Stack direction="row" gap={1} alignItems="center">
                    <Chip
                      size="small"
                      label={appraisal.status}
                      color={statusColors[appraisal.status] ?? 'default'}
                    />
                    {appraisal.status === 'Awaiting HR Review' && (
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => setReviewingAppraisalID(appraisal._id)}
                      >
                        Review
                      </Button>
                    )}
                    {appraisal.status === 'Completed' && (
                      <Button
                        size="small"
                        onClick={() => setReviewingAppraisalID(appraisal._id)}
                      >
                        View
                      </Button>
                    )}
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}

      <BigModal
        open={!!reviewingAppraisalID}
        onClose={() => setReviewingAppraisalID(null)}
      >
        <Stack padding={2}>
          {reviewingAppraisalID && (
            <AppraisalReview
              appraisalID={reviewingAppraisalID}
              onCompleted={handleCompleted}
            />
          )}
        </Stack>
      </BigModal>
    </Stack>
  )
}

export default AppraisalsTab
